// @ts-check

// Shared imports
const { ExtendableError } = require('shared/error-utils')
const { DataURI } = require('shared/uri-utils')

/**
 * @typedef {Object} Rect
 * @property {number} x
 * @property {number} y
 * @property {number} width
 * @property {number} height
*/

/**
 * @typedef {Object} PreviewProps
 * @property {number} maxFileSize
 * @property {number} minWidth
 * @property {number} minHeight
 * @property {Array.<string>} supportedTypes
 */

/**
 * @typedef {Object} PreviewState
 * @property {null} crop
 * @property {PreviewError|null} error
 * @property {HTMLImageElement|null} image
 */

const byteMagnitudes = [
  'Bytes',
  'Kilobytes',
  'Megabytes',
  'Gigabytes',
  'Terrabytes'
]

/**
 * Format a byte count into a human-readable string
 * @param {number} byteCount
 * @param {number} decimalPlaces
 * @returns {string} Example: `1.44 Megabytes`
 */
function formatBytes(byteCount, decimalPlaces = 2) {
  if (!byteCount) {
    return '0 Bytes'
  }
  decimalPlaces = Math.max(decimalPlaces, 0)
  const k = 1024 // bytes per magnitude step
  const magnitudeIndex = Math.min(
    Math.floor(Math.log(byteCount) / Math.log(k)),
    byteMagnitudes.length - 1
  )
  const magnitudeCount = byteCount / Math.pow(k, magnitudeIndex)
  const formattedCount = magnitudeCount.toFixed(decimalPlaces)
  return `${formattedCount} ${byteMagnitudes[magnitudeIndex]}`
}

/**
 * Convert a File into an Image element
 * @param {File} file
 * @returns {Promise.<HTMLImageElement>}
 */
async function readFileAsImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader
    const image = new Image
    reader.onload = () => {
      // @ts-ignore - readAsDataURL will always result in a string
      image.src = reader.result
    }
    reader.onerror = () => {
      reject(reader.error || new Error('Image file reading failed'))
    }
    image.onload = () => {
      resolve(image)
    }
    image.onerror = (/** @type {ErrorEvent} */ event) => {
      reject(event.error || new Error('Data URL parsing failed'))
    }
    reader.readAsDataURL(file)
  })
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {Object} [options]
 * @param {string} [options.fileType]
 * @param {number} [options.quality]
 * @returns {Promise.<Blob>}
 */
async function convertCanvasToBlob(canvas, options = {}) {
  const fileType = options.fileType || 'image/jpeg'
  const quality = options.quality || .85

  if (typeof canvas.toBlob === 'function') {
    // Prefer built-in toBlob method if it exists
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob(resolve, fileType, quality)
      } catch (err) {
        reject(err)
      }
    })
  }
  // Fall-back to (slower) two-step conversion to Blob
  const uri = canvas.toDataURL(fileType, quality)
  return DataURI.toBlob(uri)
}

/**
 * Generate initial `PreviewState` for `UploadImageModal`.
 *
 * This is pretty tightly-coupled with the `UploadImageModal`
 * "dumb component" implementation.
 * @param {PreviewProps} props
 * @param {File} file
 * @returns {Promise.<PreviewState>}
 */
async function processFilePreviewState(props, file) {
  if (!file) {
    return { crop: null, error: null, image: null }
  }
  try {
    if (!props.supportedTypes.includes(file.type)) {
      throw new PreviewError(`Unsupported file type: ${file.type}`)
    }
    if (file.size >= props.maxFileSize) {
      throw new PreviewError(`File is too large: ${formatBytes(file.size, 2)}`)
    }
    const image = await readFileAsImage(file)
    if (image.width < props.minWidth || image.height < props.minHeight) {
      throw new PreviewError(`Image is too small: ${image.width}×${image.height} pixels`)
    }
    return { crop: null, error: null, image }
  } catch (err) {
    if (err instanceof PreviewError) {
      return { crop: null, error: err, image: null }
    }
    throw err
  }
}

/**
 * @param {HTMLImageElement} image
 * @param {Rect} cropPixels
 * @returns {Promise.<Blob>}
 */
async function processImageFinalCrop(image, cropPixels) {
  if (
    cropPixels.x === 0 &&
    cropPixels.y === 0 &&
    cropPixels.width === image.width &&
    cropPixels.height === image.height
  ) {
    return DataURI.toBlob(image.src)
  }

  const canvas = document.createElement('canvas')
  canvas.width = cropPixels.width
  canvas.height = cropPixels.height
  const ctx = canvas.getContext('2d')

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropPixels.width,
    cropPixels.height
  )

  return convertCanvasToBlob(canvas)
}

module.exports = {
  convertCanvasToBlob,
  formatBytes,
  processFilePreviewState,
  processImageFinalCrop,
  readFileAsImage
}

/**
 * An error caused during `processFilePreviewState`
 * @class
 * @extends ExtendableError
 */
class PreviewError extends ExtendableError {
  // Sub-classing just for a unique error identifier
}
